/**
 * TrustGate Action Request Modal
 * 
 * Create new verification requests (PAYMENT_RELEASE)
 * FAIL-CLOSED: All requests go through createActionRequest Cloud Function
 */

import React, { useState } from 'react';
import {
  createActionRequest,
  validateActionRequest,
  formatActionAmount,
  type CreateActionRequestParams,
} from '../../data/TrustGateActionService';

interface ActionRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (requestId: string, proofId?: string) => void;
}

export const ActionRequestModal: React.FC<ActionRequestModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<CreateActionRequestParams>({
    amount: 0,
    currency: 'USD',
    destination: '',
    memo: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{
    requestId: string;
    status: string;
    reasonCode?: string;
  } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate
    const validation = validateActionRequest(formData);
    if (!validation.valid) {
      setError(validation.error || 'Invalid request');
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await createActionRequest(formData);

      if (!response.ok) {
        setError(response.reasonCode || 'Request failed');
        return;
      }

      setSuccess({
        requestId: response.requestId,
        status: response.status,
        reasonCode: response.reasonCode,
      });

      // Auto-close after 2 seconds on success
      setTimeout(() => {
        onSuccess(response.requestId);
        handleClose();
      }, 2000);
    } catch (err) {
      console.error('[ActionRequestModal] Submit error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      amount: 0,
      currency: 'USD',
      destination: '',
      memo: '',
    });
    setError(null);
    setSuccess(null);
    onClose();
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Convert dollars to cents
    const dollars = parseFloat(value) || 0;
    const cents = Math.round(dollars * 100);
    setFormData({ ...formData, amount: cents });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-[#1e293b] border border-white/10 rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-[#f8fafc]">Create Bound Intent</h2>
          <button
            onClick={handleClose}
            disabled={submitting}
            className="text-[#cbd5e1] hover:text-[#f8fafc] transition-colors disabled:opacity-50"
          >
            ✕
          </button>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-4 p-4 bg-[#00d4aa]/10 border border-[#00d4aa]/30 rounded-lg">
            <div className="text-[#00d4aa] font-semibold mb-1">
              {success.status === 'PENDING' ? '✓ Intent Submitted' : '⚠️ Binding Denied'}
            </div>
            <div className="text-sm text-[#cbd5e1] font-mono">{success.requestId}</div>
            {success.reasonCode && (
              <div className="text-xs text-[#cbd5e1] mt-1">Reason: {success.reasonCode}</div>
            )}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-[#ef4444]/10 border border-[#ef4444]/30 rounded-lg text-[#ef4444]">
            ⚠️ {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-[#cbd5e1] mb-2">
              Intent Amount *
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#cbd5e1]">$</span>
              <input
                type="number"
                step="0.01"
                min="0.01"
                required
                disabled={submitting || !!success}
                value={formData.amount > 0 ? (formData.amount / 100).toFixed(2) : ''}
                onChange={handleAmountChange}
                placeholder="0.00"
                className="w-full pl-8 pr-4 py-2 bg-[#0f172a] border border-white/10 rounded-lg text-[#f8fafc] placeholder-[#64748b] focus:outline-none focus:border-[#00d4aa] focus:ring-1 focus:ring-[#00d4aa] disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
            {formData.amount > 0 && (
              <div className="mt-1 text-xs text-[#cbd5e1]">
                {formatActionAmount(formData.amount, formData.currency)}
              </div>
            )}
          </div>

          {/* Currency */}
          <div>
            <label className="block text-sm font-medium text-[#cbd5e1] mb-2">
              Currency *
            </label>
            <select
              required
              disabled={submitting || !!success}
              value={formData.currency}
              onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
              className="w-full px-4 py-2 bg-[#0f172a] border border-white/10 rounded-lg text-[#f8fafc] focus:outline-none focus:border-[#00d4aa] focus:ring-1 focus:ring-[#00d4aa] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="CAD">CAD</option>
            </select>
          </div>

          {/* Destination */}
          <div>
            <label className="block text-sm font-medium text-[#cbd5e1] mb-2">
              Destination *
            </label>
            <input
              type="text"
              required
              disabled={submitting || !!success}
              value={formData.destination}
              onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
              placeholder="vendor_id or destination_id"
              className="w-full px-4 py-2 bg-[#0f172a] border border-white/10 rounded-lg text-[#f8fafc] placeholder-[#64748b] focus:outline-none focus:border-[#00d4aa] focus:ring-1 focus:ring-[#00d4aa] disabled:opacity-50 disabled:cursor-not-allowed font-mono text-sm"
            />
          </div>

          {/* Memo */}
          <div>
            <label className="block text-sm font-medium text-[#cbd5e1] mb-2">
              Context (Optional)
            </label>
            <textarea
              disabled={submitting || !!success}
              value={formData.memo}
              onChange={(e) => setFormData({ ...formData, memo: e.target.value })}
              placeholder="Provide context that helps governance evaluate this intent..."
              maxLength={500}
              rows={3}
              className="w-full px-4 py-2 bg-[#0f172a] border border-white/10 rounded-lg text-[#f8fafc] placeholder-[#64748b] focus:outline-none focus:border-[#00d4aa] focus:ring-1 focus:ring-[#00d4aa] disabled:opacity-50 disabled:cursor-not-allowed resize-none"
            />
            <div className="mt-1 text-xs text-[#64748b]">
              {formData.memo?.length || 0} / 500 characters
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-white/5 hover:bg-white/10 text-[#f8fafc] border border-white/10 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !!success}
              className="flex-1 px-4 py-2 bg-[#00d4aa] hover:bg-[#00a8cc] text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting...' : success ? '✓ Submitted' : 'Submit for Binding'}
            </button>
          </div>
        </form>

        {/* Info */}
        <div className="mt-6 p-3 bg-white/5 border border-white/10 rounded-lg text-xs text-[#cbd5e1]">
          <div className="font-semibold mb-1">ℹ️ Binding Flow</div>
          <div>1. Intent submitted and evaluated by governance</div>
          <div>2. If permitted, intent awaits authority binding</div>
          <div>3. Authority issues binding receipt to authorize execution</div>
          <div>4. Bound intent can then be executed with proof</div>
        </div>
      </div>
    </div>
  );
};
